/**
 * Probabilidade da roleta em porcentagem (0–100), formato brasileiro (vírgula decimal).
 */

export const ROULETTE_PROBABILITY_DECIMAL_PLACES = 6

const SCALE = 1_000_000

const TARGET_SUM_SCALED = 100 * SCALE

export function roundRouletteProbabilityPercent(value: number): number {
  if (!Number.isFinite(value)) {
    return 0
  }
  const clamped = Math.min(100, Math.max(0, value))
  const factor = 10 ** ROULETTE_PROBABILITY_DECIMAL_PLACES
  return Math.round(clamped * factor) / factor
}

/**
 * Sanitiza texto digitado: apenas dígitos e no máximo uma vírgula decimal.
 */
export function sanitizeRouletteProbabilityInput(raw: string): string {
  let result = ''
  let hasComma = false

  for (const char of raw.trim()) {
    if (char >= '0' && char <= '9') {
      result += char
      continue
    }
    if (char === ',' && !hasComma) {
      const fraction = result.split(',')[1]
      if (fraction !== undefined && fraction.length >= ROULETTE_PROBABILITY_DECIMAL_PLACES) {
        continue
      }
      hasComma = true
      result += char
    }
  }

  if (result.startsWith(',')) {
    result = `0${result}`
  }

  const commaIndex = result.indexOf(',')
  if (commaIndex >= 0) {
    const intPart = result.slice(0, commaIndex)
    const fracPart = result.slice(commaIndex + 1, commaIndex + 1 + ROULETTE_PROBABILITY_DECIMAL_PLACES)
    result = `${intPart},${fracPart}`
  }

  return result
}

/**
 * Parse durante digitação: mantém vírgula e zeros à direita no rascunho;
 * aceita valor incompleto (ex.: "0," ou "0,00") para atualizar a soma em tempo real.
 */
export function parseRouletteProbabilityForLiveUpdate(raw: string): number | null {
  const sanitized = sanitizeRouletteProbabilityInput(raw)
  if (sanitized === '' || sanitized === ',') {
    return null
  }
  if (sanitized.endsWith(',')) {
    const intPart = sanitized.slice(0, -1)
    if (intPart === '') {
      return 0
    }
    return parseRouletteProbabilityPercentInput(intPart)
  }
  return parseRouletteProbabilityPercentInput(sanitized)
}

/**
 * Interpreta string no formato brasileiro (vírgula) ou ponto (API legada).
 */
export function parseRouletteProbabilityPercentInput(raw: string): number | null {
  const trimmed = raw.trim()
  if (trimmed === '') {
    return null
  }

  if (!/^[\d.,]+$/.test(trimmed)) {
    return null
  }

  let normalized = trimmed
  const commaPos = normalized.lastIndexOf(',')
  const dotPos = normalized.lastIndexOf('.')

  if (commaPos >= 0 && dotPos >= 0) {
    if (commaPos > dotPos) {
      normalized = normalized.replace(/\./g, '').replace(',', '.')
    } else {
      normalized = normalized.replace(/,/g, '')
    }
  } else if (commaPos >= 0) {
    normalized = normalized.replace(',', '.')
  }

  if ((normalized.match(/\./g) ?? []).length > 1) {
    return null
  }

  if (!/^\d+(\.\d+)?$/.test(normalized)) {
    return null
  }

  const parsed = Number.parseFloat(normalized)
  if (!Number.isFinite(parsed)) {
    return null
  }

  return roundRouletteProbabilityPercent(parsed)
}

/**
 * Interpreta valor ao sair do campo, incluindo vírgula final (ex.: "12," → 12).
 */
export function parseRouletteProbabilityPercentOnBlur(raw: string): number | null {
  const sanitized = sanitizeRouletteProbabilityInput(raw)
  if (sanitized === '' || sanitized === ',') {
    return null
  }
  if (sanitized.endsWith(',')) {
    return parseRouletteProbabilityPercentInput(`${sanitized}0`)
  }
  return parseRouletteProbabilityPercentInput(sanitized)
}

function formatPercentValue(value: number, clampToHundred: boolean): string {
  if (!Number.isFinite(value)) {
    return '0'
  }
  const factor = 10 ** ROULETTE_PROBABILITY_DECIMAL_PLACES
  const rounded = clampToHundred
    ? roundRouletteProbabilityPercent(value)
    : Math.round(value * factor) / factor
  const fixed = rounded.toFixed(ROULETTE_PROBABILITY_DECIMAL_PLACES)
  const trimmed = fixed.replace(/\.?0+$/, '')
  return trimmed.replace('.', ',')
}

/** Formata percentual de fatia (0–100). */
export function formatRouletteProbabilityPercentBr(value: number): string {
  return formatPercentValue(value, true)
}

/** Formata total agregado (pode exceder 100% na interface). */
export function formatRouletteProbabilityTotalBr(value: number): string {
  return formatPercentValue(value, false)
}

export function scaleRouletteProbabilityPercent(value: number): number {
  return Math.round(roundRouletteProbabilityPercent(value) * SCALE)
}

export function probabilitiesSumValid(
  prizes: ReadonlyArray<{ probability: number }>
): boolean {
  if (prizes.length === 0) {
    return false
  }

  let sum = 0
  for (const prize of prizes) {
    if (!Number.isFinite(prize.probability)) {
      return false
    }
    sum += scaleRouletteProbabilityPercent(prize.probability)
  }

  return sum === TARGET_SUM_SCALED
}

export function sumRouletteProbabilitiesScaled(
  prizes: ReadonlyArray<{ probability: number }>
): number {
  let sumScaled = 0
  for (const prize of prizes) {
    sumScaled += scaleRouletteProbabilityPercent(prize.probability)
  }
  return sumScaled
}

export function sumOthersRouletteProbabilitiesScaled(
  prizes: ReadonlyArray<{ probability: number }>,
  excludeIndex: number
): number {
  let sumScaled = 0
  prizes.forEach((prize, index) => {
    if (index !== excludeIndex) {
      sumScaled += scaleRouletteProbabilityPercent(prize.probability)
    }
  })
  return sumScaled
}

export function sumRouletteProbabilitiesPercent(
  prizes: ReadonlyArray<{ probability: number }>
): number {
  return sumRouletteProbabilitiesScaled(prizes) / SCALE
}

/** Restante para totalizar 100% a partir da soma já agregada das outras fatias. */
export function remainderRouletteProbabilityPercent(sumOthers: number): number {
  const othersScaled = scaleRouletteProbabilityPercent(sumOthers)
  const remainderScaled = Math.max(0, TARGET_SUM_SCALED - othersScaled)
  return remainderScaled / SCALE
}

/** Restante para totalizar 100% somando cada fatia na escala fixa (mais confiável). */
export function remainderRouletteProbabilityPercentForSlots(
  slots: ReadonlyArray<{ probability: number }>,
  excludeIndex: number
): number {
  let sumScaled = 0
  slots.forEach((slot, index) => {
    if (index !== excludeIndex) {
      sumScaled += scaleRouletteProbabilityPercent(slot.probability)
    }
  })
  return Math.max(0, TARGET_SUM_SCALED - sumScaled) / SCALE
}

/** Converte probabilidade da API (decimal 0–1 ou percentual) para % no formulário. */
export function rouletteProbabilityToPercent(value: unknown): number {
  const n = typeof value === 'string' ? Number.parseFloat(value) : Number(value)
  if (!Number.isFinite(n) || n < 0) {
    return 0
  }
  const percent = n > 1 ? n : n * 100
  return roundRouletteProbabilityPercent(percent)
}
